// eslint-disable-next-line @typescript-eslint/triple-slash-reference -- ambient module declaration; must travel with this file when consumers type-check it from source
/// <reference path="../../refparser.d.ts" />
import OsisFormatter from 'bible-reference-formatter/es6/osisFormatter.js';
import { bcv_parser } from 'bible-passage-reference-parser/esm/bcv_parser.js';
import * as lang from 'bible-passage-reference-parser/esm/lang/en.js';
import {
  OSIS_TO_USFM_BOOK,
  formatOsisPoint,
  formatUsfmPoint,
  parseOsisSegment,
  parseUsfmSegment,
} from './usfm-helpers.js';
import { BoundedCache } from './bounded-cache.js';

/*
 * Parsing is relatively expensive - building a parser takes ~0.5ms (and
 * tens of ms for the first one), and each parse ~1ms - and callers tend to
 * work with the same few references over and over (e.g. re-rendering a list
 * of passages, where each item formats its reference, validates it, counts
 * its verses, ...). So parsers and the formatter are built once per
 * configuration and reused (a parser resets its state on every `parse()`),
 * and the results of the core string-to-string conversions are cached.
 */

const lazy = <T>(create: () => T): (() => T) => {
  let instance: T | undefined;
  return () => (instance ??= create());
};

/**
 * Compacts to the book level (`b`) - used to check validity, and to strip
 * "unnecessary" verses when formatting
 */
const getCompactParser = lazy(() =>
  new bcv_parser(lang).set_options({
    osis_compaction_strategy: 'b',
    book_sequence_strategy: 'include',
    book_alone_strategy: 'full',
    book_range_strategy: 'include',
  })
);

/**
 * Compacts to the verse level (`bcv`) - used to produce OSIS strings
 */
const getOsisParser = lazy(() =>
  new bcv_parser(lang).set_options({
    osis_compaction_strategy: 'bcv',
    book_sequence_strategy: 'include',
    book_alone_strategy: 'full',
    book_range_strategy: 'include',
  })
);

const getRefsFromStringParser = lazy(() =>
  new bcv_parser(lang).set_options({
    osis_compaction_strategy: 'bcv',
    book_sequence_strategy: 'ignore',
    book_range_strategy: 'ignore',
    sequence_combination_strategy: 'separate',
    book_alone_strategy: 'ignore',
    invalid_passage_strategy: 'ignore',
  })
);

/**
 * Book order, and the number of verses in each chapter of each book
 */
const getTranslationInfo = lazy(() => new bcv_parser(lang).translation_info());

const compactOsisCache = new BoundedCache();
const osisCache = new BoundedCache();
const formattedRefCache = new BoundedCache();

// `\u0000` can't appear in a reference, so keys can't collide
const cacheKey = (...parts: (string | undefined)[]) => parts.map((p) => p ?? '').join('\u0000');

const parseToOsis = (parser: bcv_parser, ref: string, context: string | undefined): string =>
  context ? parser.parse_with_context(ref, context).osis() : parser.parse(ref).osis();

/**
 * `ref` (with optional `context`) as a book-compacted OSIS string, or an
 * empty string if it isn't a valid reference
 */
const getCompactOsis = (ref: string, context?: string): string =>
  compactOsisCache.get(cacheKey(ref, context), () => parseToOsis(getCompactParser(), ref, context));

/**
 * Data type to cover a range of verses, based on OSIS ref
 */
export interface OsisRange {
  startOsisString: string;
  endOsisString: string;
}

interface IsRefValidOptions {
  context?: string | undefined;
}

/**
 * Checks whether a string contains a valid reference (or OSIS). Can
 * optionally be passed **context** in which to check the string.
 *
 * @example
 * isRefValid('Gen.1.1') // true
 * isRefValid('Genesis 1:1') // true
 * isRefValid('verse 1') // false
 * isRefValid('verse 1', { context = undefined }) // false
 * isRefValid('verse 1', { context = 'Matt 1' }) // true
 * isRefValid('1', { context = 'Matt 1' }) // true
 *
 * @param ref Reference to check (OSIS works)
 * @param context Larger context within the check should be performed, if any
 * @returns Boolean indicating validity
 */
export const isRefValid = (ref: string | undefined, { context = undefined }: IsRefValidOptions = {}): boolean => {
  if (!ref) return false;

  return getCompactOsis(ref, context).length > 0;
};

interface GetOsisForRefOptions {
  context?: string | undefined;
}

/**
 * Takes a string containing a reference and returns a valid OSIS
 * reference for that string (or an empty string if the reference
 * isn't valid).
 *
 * Uses same logic as {@link isRefValid} for context.
 *
 * Can safely be called with a string that's already a valid OSIS
 * reference, in which case that same string will be returned.
 *
 * @param ref The reference (can be OSIS or not)
 * @param context Context for the reference, if any
 * @returns OSIS string, or empty string if no valid match
 */
export const getOsisForRef = (ref: string, { context = undefined }: GetOsisForRefOptions = {}): string =>
  osisCache.get(cacheKey(ref, context), () => parseToOsis(getOsisParser(), ref, context));

interface GetRefForOsisOptions {
  includeVerses?: boolean;
}

/**
 * Internal function to return a formatted reference string for an OSIS
 * string. Can optionally format the returned reference to strip out
 * unnecessary verses.
 *
 * Using mostly OOB settings from `bible-reference-formatter` except always
 * n-dash instead of m-dash.
 *
 * @example
 * // Genesis 1:1–31
 * getRefForOsis('Gen.1.1-Gen.1.31')
 * getRefForOsis('Gen.1.1-Gen.1.31', { includeVerses = true })
 *
 * @example
 * // Genesis 1
 * getRefForOsis('Gen.1.1-Gen.1.31', { includeVerses = false })
 *
 * @example
 * // always Rev 1:1
 * getRefForOsis('Rev.1.1')
 * getRefForOsis('Rev.1.1', { includeVerses = true })
 * getRefForOsis('Rev.1.1', { includeVerses = false })
 *
 * @param osisString String to be formatted
 * @param includeVerses Whether to include "unnecessary" verses
 * @returns Formatted string
 */
const getOsisFormatter = lazy(() => {
  const osisFormatter = new OsisFormatter();
  osisFormatter.setBooks({
    Gen: ['Genesis'],
    Exod: ['Exodus'],
    Lev: ['Leviticus'],
    Num: ['Numbers'],
    Deut: ['Deuteronomy'],
    Josh: ['Joshua'],
    Judg: ['Judges'],
    Ruth: ['Ruth'],
    '1Sam': ['1 Samuel'],
    '2Sam': ['2 Samuel'],
    '1Kgs': ['1 Kings'],
    '2Kgs': ['2 Kings'],
    '1Chr': ['1 Chronicles'],
    '2Chr': ['2 Chronicles'],
    Ezra: ['Ezra'],
    Neh: ['Nehemiah'],
    Esth: ['Esther'],
    Job: ['Job'],
    Ps: ['Psalm', 'Psalms'],
    Prov: ['Proverbs'],
    Eccl: ['Ecclesiastes'],
    Song: ['Song of Solomon'],
    Isa: ['Isaiah'],
    Jer: ['Jeremiah'],
    Lam: ['Lamentations'],
    Ezek: ['Ezekiel'],
    Dan: ['Daniel'],
    Hos: ['Hosea'],
    Joel: ['Joel'],
    Amos: ['Amos'],
    Obad: ['Obadiah'],
    Jonah: ['Jonah'],
    Mic: ['Micah'],
    Nah: ['Nahum'],
    Hab: ['Habakkuk'],
    Zeph: ['Zephaniah'],
    Hag: ['Haggai'],
    Zech: ['Zechariah'],
    Mal: ['Malachi'],
    Matt: ['Matthew'],
    Mark: ['Mark'],
    Luke: ['Luke'],
    John: ['John'],
    Acts: ['Acts'],
    Rom: ['Romans'],
    '1Cor': ['1 Corinthians'],
    '2Cor': ['2 Corinthians'],
    Gal: ['Galatians'],
    Eph: ['Ephesians'],
    Phil: ['Philippians'],
    Col: ['Colossians'],
    '1Thess': ['1 Thessalonians'],
    '2Thess': ['2 Thessalonians'],
    '1Tim': ['1 Timothy'],
    '2Tim': ['2 Timothy'],
    Titus: ['Titus'],
    Phlm: ['Philemon'],
    Heb: ['Hebrews'],
    Jas: ['James'],
    '1Pet': ['1 Peter'],
    '2Pet': ['2 Peter'],
    '1John': ['1 John'],
    '2John': ['2 John'],
    '3John': ['3 John'],
    Jude: ['Jude'],
    Rev: ['Revelation'],
    Tob: ['Tobit'],
    Jdt: ['Judith'],
    GkEsth: ['Greek Esther'],
    EsthGr: ['Greek Esther'],
    AddEsth: ['Additions to Esther'],
    Wis: ['Wisdom of Solomon'],
    Sir: ['Sirach'],
    Bar: ['Baruch'],
    EpJer: ['Epistle of Jeremiah'],
    DanGr: ['Greek Daniel'],
    SgThree: ['Song of the Three Jews'],
    PrAzar: ['Prayer of Azariah'],
    Sus: ['Susanna'],
    Bel: ['Bel and the Dragon'],
    '1Macc': ['1 Maccabees'],
    '2Macc': ['2 Maccabees'],
    '3Macc': ['3 Maccabees'],
    '4Macc': ['4 Maccabees'],
    PrMan: ['Prayer of Manasseh'],
    '1Esd': ['1 Esdras'],
    '2Esd': ['2 Esdras'],
    Ps151: ['Psalm 151'],
    AddPs: ['Psalm 151'],
    // Psalms
    'Ps.$chapters': ['Psalm', 'Psalms'],
    // Ranges
    '1Sam-2Sam': ['1\u20132 Samuel'],
    '1Kgs-2Kgs': ['1\u20132 Kings'],
    '1Chr-2Chr': ['1\u20132 Chronicles'],
    '1Cor-2Cor': ['1\u20132 Corinthians'],
    '1Thess-2Thess': ['1\u20132 Thessalonians'],
    '1Tim-2Tim': ['1\u20132 Timothy'],
    '1Pet-2Pet': ['1\u20132 Peter'],
    '1John-2John': ['1\u20132 John'],
    '1John-3John': ['1\u20133 John'],
    '2John-3John': ['2\u20133 John'],
    '1Macc-2Macc': ['1\u20132 Maccabees'],
    '1Macc-3Macc': ['1\u20133 Maccabees'],
    '1Macc-4Macc': ['1\u20134 Maccabees'],
    '2Macc-3Macc': ['2\u20133 Maccabees'],
    '2Macc-4Macc': ['2\u20134 Maccabees'],
    '3Macc-4Macc': ['3\u20134 Maccabees'],
    '1Esd-2Esd': ['1\u20132 Esdras'],
    // Sequences
    '1Sam,2Sam': ['1 and 2 Samuel'],
    '1Kgs,2Kgs': ['1 and 2 Kings'],
    '1Chr,2Chr': ['1 and 2 Chronicles'],
    '1Cor,2Cor': ['1 and 2 Corinthians'],
    '1Thess,2Thess': ['1 and 2 Thessalonians'],
    '1Tim,2Tim': ['1 and 2 Timothy'],
    '1Pet,2Pet': ['1 and 2 Peter'],
    '1John,2John': ['1 and 2 John'],
    '1John,3John': ['1 and 3 John'],
    '2John,3John': ['2 and 3 John'],
    '1Macc,2Macc': ['1 and 2 Maccabees'],
    '1Macc,2Macc,3Macc': ['1, 2, and 3 Maccabees'],
    '1Macc,2Macc,3Macc,4Macc': ['1, 2, 3, and 4 Maccabees'],
    '1Macc,3Macc': ['1 and 3 Maccabees'],
    '1Macc,3Macc,4Macc': ['1, 3, and 4 Maccabees'],
    '1Macc,4Macc': ['1 and 4 Maccabees'],
    '2Macc,3Macc': ['2 and 3 Maccabees'],
    '2Macc,3Macc,4Macc': ['2, 3, and 4 Maccabees'],
    '2Macc,4Macc': ['2 and 4 Maccabees'],
    '3Macc,4Macc': ['3 and 4 Maccabees'],
    '1Esd,2Esd': ['1 and 2 Esdras'],
  });
  osisFormatter.setOptions({
    ',': '; ',
    'b,c': '; $chapters ',
    'b,v': '; $b ',
    'c,v': '; $c:',
    'v,c': '; $chapters ',
    'v,cv': '; ',
    'v,v': ', ',
    $chapters: ['ch.', 'chs.'],
    $verses: ['v.', 'vv.'],
    singleChapterFormat: 'b',
    '-': '\u2013',
    'b-c': '\u2013$chapters ',
    'b-v': '\u2013$b ',
    'c-v': '\u2013$c:',
    'v-c': '\u2013$chapters ',
    'v-cv': '\u2013',
    'v-v': '\u2013',
    '^c': '$chapters ',
    'b1^v': '$verses ',
    '^v': '$verses ',
  });

  return osisFormatter;
});

const getRefForOsis = (osisString: string, { includeVerses = true }: GetRefForOsisOptions = {}): string =>
  formattedRefCache.get(cacheKey(osisString, includeVerses ? 'v' : ''), () =>
    getOsisFormatter().format(includeVerses ? osisString : getCompactOsis(osisString))
  );

/**
 * Same as {@link getRefForOsis} (and uses that function under the hood),
 * except that the input is an array of {@link OsisBibleVerse} objects
 * instead of an OSIS string.
 *
 * @param verses Array of verses
 * @returns Formatted string
 */
export const getRefForOsisVerses = (verses?: OsisBibleVerse[]): string => {
  if (!verses || verses.length === 0) return '';

  const joinedOsis = verses.map((v) => v.osis + ',').join('');
  return getRefForOsis(getOsisForRef(joinedOsis));
};

/**
 * Parses an OSIS string to pull out one or more **ranges** for that string.
 *
 * @example
 * // single range, where the start and end are both Gen 1:1
 * getRangesForOsis('Gen.1.1')
 *
 * @example
 * // single range, where the start is Gen 1:1
 * // and the end is Gen 1:2
 * getRangesForOsis('Gen.1.1-Gen.1.2')
 *
 * @example
 * // two ranges: Gen 1:1 to 1:2 and Gen 1:5 to 1:5
 * getRangesForOsis('Gen.1.1-Gen.1.2,Gen.1.5')
 *
 * @param rawOsisString The OSIS string, containing one or more ranges
 * @returns Array of objects representing each range
 */
export const getRangesForOsis = (rawOsisString: string): OsisRange[] => {
  if (!isRefValid(rawOsisString)) {
    return [];
  }

  const properlyFormattedOsis = getOsisForRef(rawOsisString);

  const returnArray: OsisRange[] = [];
  const passageArray = properlyFormattedOsis.split(',');

  for (const p of passageArray) {
    const rawRange = p.split('-');

    const range: OsisRange = {
      startOsisString: rawRange[0],
      endOsisString: rawRange[rawRange.length - 1],
    };

    returnArray.push(range);
  }

  return returnArray;
};

interface GetFormattedRefOptions {
  includeVerses?: boolean;
  context?: string | undefined;
}

/**
 * Takes a string (could be OSIS or human-readable) and returns a
 * formatted version.
 *
 * Follows same logic as {@link getRefForOsis} for the `includeVerses`
 * param, and the same logic as {@link isRefValid} for `context`.
 *
 * @param osisOrRef String containing the reference, OSIS or not
 * @param includeVerses Whether "optional" verses should be included in the result
 * @param context Larger context for the reference if any
 * @returns Formatted string for the reference, or an empty string if the ref isn't valid
 */
export const getFormattedRef = (
  osisOrRef: string,
  { includeVerses = true, context = undefined }: GetFormattedRefOptions = {}
): string => {
  if (!isRefValid(osisOrRef, { context })) {
    return '';
  }

  return getRefForOsis(getOsisForRef(osisOrRef, { context }), { includeVerses });
};

export interface OsisBibleVerse {
  osis: string;
  newTestament: boolean;
}
export interface GetFormattedRefWithVersesResponse {
  formattedRef: string;
  verses: OsisBibleVerse[];
}

/**
 * Same as {@link getFormattedRef} except that a more complex
 * response is returned, with a list of all verses in the reference.
 */
export const getFormattedRefWithVerses = (
  osisOrRef: string,
  { includeVerses = true, context = undefined }: GetFormattedRefOptions = {}
): GetFormattedRefWithVersesResponse => {
  if (!isRefValid(osisOrRef, { context })) {
    return {
      formattedRef: '',
      verses: [],
    };
  }

  const osisString = getOsisForRef(osisOrRef, { context });
  const formattedRef = getRefForOsis(osisString, { includeVerses });
  const ranges = getRangesForOsis(osisString);

  const { chapters, order, books: bookNames } = getTranslationInfo();
  const malachiOrder = order['Mal'];

  const verses: OsisBibleVerse[] = [];

  for (const range of ranges) {
    const [startBook, startChStr, startVStr] = range.startOsisString.split('.');
    const [endBook, endChStr, endVStr] = range.endOsisString.split('.');

    const startCh = parseInt(startChStr);
    const startV = parseInt(startVStr);
    const endCh = parseInt(endChStr);
    const endV = parseInt(endVStr);

    if (startBook === endBook) {
      const newTestament = order[startBook] > malachiOrder;
      if (startCh === endCh) {
        for (let v = startV; v <= endV; v++) {
          verses.push({ osis: `${startBook}.${startCh}.${v}`, newTestament });
        }
      } else {
        for (let v = startV; v <= chapters[startBook][startCh - 1]; v++) {
          verses.push({ osis: `${startBook}.${startCh}.${v}`, newTestament });
        }
        for (let c = startCh + 1; c < endCh; c++) {
          for (let v = 1; v <= chapters[startBook][c - 1]; v++) {
            verses.push({ osis: `${startBook}.${c}.${v}`, newTestament });
          }
        }
        for (let v = 1; v <= endV; v++) {
          verses.push({ osis: `${endBook}.${endCh}.${v}`, newTestament });
        }
      }
    } else {
      // Verses from the start book
      const startBookIsNT = order[startBook] > malachiOrder;
      for (let v = startV; v <= chapters[startBook][startCh - 1]; v++) {
        verses.push({ osis: `${startBook}.${startCh}.${v}`, newTestament: startBookIsNT });
      }
      for (let c = startCh + 1; c <= chapters[startBook].length; c++) {
        for (let v = 1; v <= chapters[startBook][c - 1]; v++) {
          verses.push({ osis: `${startBook}.${c}.${v}`, newTestament: startBookIsNT });
        }
      }

      // Verses from intermediate books
      const startBookOrder = order[startBook];
      const endBookOrder = order[endBook];
      for (let b = startBookOrder; b < endBookOrder - 1; b++) {
        const bookName = bookNames[b - 1];
        const newTestament = order[bookName] > malachiOrder;
        for (let c = 1; c <= chapters[bookName].length; c++) {
          for (let v = 1; v <= chapters[bookName][c - 1]; v++) {
            verses.push({ osis: `${bookName}.${c}.${v}`, newTestament });
          }
        }
      }

      // Verses from the end book
      const endBookIsNT = order[endBook] > malachiOrder;
      for (let c = 1; c < endCh; c++) {
        for (let v = 1; v <= chapters[endBook][c - 1]; v++) {
          verses.push({ osis: `${endBook}.${c}.${v}`, newTestament: endBookIsNT });
        }
      }
      for (let v = 1; v <= endV; v++) {
        verses.push({ osis: `${endBook}.${endCh}.${v}`, newTestament: endBookIsNT });
      }
    }
  }

  return {
    formattedRef,
    verses,
  };
};

/**
 * TypeScript interface for the data returned from the
 * `osis_and_indices()` function in the underlying library
 */
interface OsisAndIndicesResult {
  osis: string;
  translations: string[];
  indices: [number, number];
}

/**
 * Interface for data returned from {@link getAllRefsFromString}
 */
export interface RefFromStringResult {
  osis: string;
  indices: [number, number];
}

interface GetAllRefsFromStringOptions {
  context?: string | undefined;
}

/**
 * Parses a string that may or may not contain valid references and,
 * for each one found, returns details:
 *
 * * The formatted OSIS string, and
 * * Details about where the reference is found in the original
 * string (i.e. start and end character indices)
 *
 * @param osisOrRef Reference string
 * @param context Context (if any)
 * @returns List of 0 or more references
 */
export const getAllRefsFromString = (
  osisOrRef: string,
  { context = undefined }: GetAllRefsFromStringOptions = {}
): RefFromStringResult[] => {
  const bcv = getRefsFromStringParser();

  const bcvResult: OsisAndIndicesResult[] = context
    ? bcv.parse_with_context(osisOrRef, context).osis_and_indices()
    : bcv.parse(osisOrRef).osis_and_indices();
  const response: RefFromStringResult[] = bcvResult.map((item) => ({
    osis: item.osis,
    indices: [item.indices[0], item.indices[1] - 1],
  }));

  return response;
};

/**
 * Converts a passage reference to USFM format, for use with the API.Bible API.
 *
 * @param origRef The OSIS string
 * @returns Passage reference in USFM format, or empty string if it can't be parsed
 */
export const convertRefToUsfm = (origRef: string, { context = undefined }: GetOsisForRefOptions = {}): string => {
  if (!origRef) return '';

  const osisString = getOsisForRef(origRef, { context });

  const segments = osisString.split('-').map((segment) => segment.trim());

  // TODO: multiple refs separated by ; ?
  if (segments.length > 2) {
    return '';
  }

  try {
    const start = parseOsisSegment(segments[0]);
    const end = segments.length === 2 ? parseOsisSegment(segments[1]) : start;

    return `${formatUsfmPoint(start)}-${formatUsfmPoint(end)}`;
  } catch {
    return '';
  }
};

/**
 * Given a single OSIS point (book.chapter.verse), returns the OSIS point
 * for the very next verse in canonical order, walking across chapter and
 * book boundaries as needed.
 *
 * Uses the same chapter/book-order metadata as {@link getFormattedRefWithVerses}.
 *
 * @example
 * getNextOsisPoint('Ps.18.21') // 'Ps.18.22'
 * getNextOsisPoint('Ps.18.50') // 'Ps.19.1' (chapter boundary)
 * getNextOsisPoint('Mal.4.6') // 'Matt.1.1' (book boundary)
 * getNextOsisPoint('Rev.22.21') // null (end of canon)
 *
 * @param osisPoint A single OSIS point, e.g. 'Ps.18.21'
 * @returns The next OSIS point, or null if there isn't one
 */
export const getNextOsisPoint = (osisPoint: string): string | null => {
  const [book, chapterStr, verseStr] = osisPoint.split('.');
  const chapter = parseInt(chapterStr);
  const verse = parseInt(verseStr);

  const { chapters, order, books: bookNames } = getTranslationInfo();

  if (!chapters[book] || !Number.isInteger(chapter) || !Number.isInteger(verse)) return null;
  if (chapter < 1 || chapter > chapters[book].length) return null;

  const versesInChapter = chapters[book][chapter - 1];
  if (verse < versesInChapter) {
    return `${book}.${chapter}.${verse + 1}`;
  }

  if (chapter < chapters[book].length) {
    return `${book}.${chapter + 1}.1`;
  }

  const nextBook = bookNames[order[book]];
  if (!nextBook || !OSIS_TO_USFM_BOOK[nextBook]) return null;

  return `${nextBook}.1.1`;
};

/**
 * Calculates the USFM passage range to request next when paginating
 * around API.Bible's 200-verse-per-request cap
 * (https://docs.api.bible/guides/rest-api/limits).
 *
 * @example
 * // 'PSA.18.22-PSA.150.6'
 * getNextPassageId('PSA.1.1-PSA.150.6', 'PSA.18.21')
 *
 * @param originalUsfmPassageId The full range originally requested, in USFM (e.g. from {@link convertRefToUsfm})
 * @param lastReturnedUsfmVerse The last verse actually included in the (truncated) response, in USFM (e.g. from a verse span's `verseOrgIds`)
 * @returns The USFM range to request next, or an empty string if there's nothing left to fetch
 */
export const getNextPassageId = (originalUsfmPassageId: string, lastReturnedUsfmVerse: string): string => {
  const segments = originalUsfmPassageId.split('-').map((segment) => segment.trim());
  if (segments.length < 1 || segments.length > 2) return '';

  try {
    const endPoint = parseUsfmSegment(segments[segments.length - 1]);
    const lastReturnedPoint = parseUsfmSegment(lastReturnedUsfmVerse);

    const endOsis = formatOsisPoint(endPoint);
    const lastReturnedOsis = formatOsisPoint(lastReturnedPoint);

    if (lastReturnedOsis === endOsis) return '';

    const nextStartOsis = getNextOsisPoint(lastReturnedOsis);
    if (!nextStartOsis) return '';

    return convertRefToUsfm(`${nextStartOsis}-${endOsis}`);
  } catch {
    return '';
  }
};

/**
 * The maximum number of verses API.Bible will return from a single
 * `/passages` request: https://docs.api.bible/guides/rest-api/limits.
 */
export const API_BIBLE_MAX_VERSES_PER_REQUEST = 200;

/**
 * Given a USFM passage range, calculates the USFM range for the first
 * `maxVerses` verses of it (the whole range, if it's shorter than that).
 *
 * This mirrors what API.Bible itself will return for a passage that's
 * too large for a single request, without needing to call the API - so
 * it can be used to pre-split a large request into cacheable chunks
 * before ever hitting the API, or to double check whether a cached
 * passage is actually complete.
 *
 * @example
 * // 'PSA.1.1-PSA.18.21'
 * getUsfmPassageChunk('PSA.1.1-PSA.150.6')
 *
 * @param usfmPassageId The full range, in USFM (e.g. from {@link convertRefToUsfm})
 * @param maxVerses Maximum number of verses in the returned chunk
 * @returns The USFM range for the first `maxVerses` verses, or an empty string if `usfmPassageId` isn't valid
 */
export const getUsfmPassageChunk = (
  usfmPassageId: string,
  maxVerses: number = API_BIBLE_MAX_VERSES_PER_REQUEST
): string => {
  const segments = usfmPassageId.split('-').map((segment) => segment.trim());
  if (segments.length < 1 || segments.length > 2) return '';

  try {
    const startPoint = parseUsfmSegment(segments[0]);
    const endPoint = segments.length === 2 ? parseUsfmSegment(segments[1]) : startPoint;

    const startOsis = formatOsisPoint(startPoint);
    const endOsis = formatOsisPoint(endPoint);

    let chunkEndOsis = startOsis;
    let versesInChunk = 1;

    while (chunkEndOsis !== endOsis && versesInChunk < maxVerses) {
      const next = getNextOsisPoint(chunkEndOsis);
      if (!next) break;

      chunkEndOsis = next;
      versesInChunk++;
    }

    return convertRefToUsfm(`${startOsis}-${chunkEndOsis}`);
  } catch {
    return '';
  }
};

/**
 * Given a USFM passage range, checks whether it represents only part of
 * a single Bible chapter. If so, returns the USFM range for that entire
 * chapter; otherwise (the passage is already a full chapter, or it spans
 * more than one chapter) returns `undefined`.
 *
 * @example
 * expandUsfmPassageToFullChapter('PSA.1.1-PSA.1.1') // 'PSA.1.1-PSA.1.6'
 * expandUsfmPassageToFullChapter('PSA.1.1-PSA.1.2') // 'PSA.1.1-PSA.1.6'
 * expandUsfmPassageToFullChapter('PSA.1.1-PSA.1.6') // undefined (already a full chapter)
 * expandUsfmPassageToFullChapter('PSA.1.1-PSA.2.1') // undefined (spans multiple chapters)
 *
 * @param usfmPassageId The USFM passage range (e.g. from {@link convertRefToUsfm})
 * @returns The USFM range for the full chapter, or undefined
 */
export const expandUsfmPassageToFullChapter = (usfmPassageId: string): string | undefined => {
  const segments = usfmPassageId.split('-').map((segment) => segment.trim());
  if (segments.length < 1 || segments.length > 2) return undefined;

  try {
    const startPoint = parseUsfmSegment(segments[0]);
    const endPoint = segments.length === 2 ? parseUsfmSegment(segments[1]) : startPoint;

    if (startPoint.book !== endPoint.book || startPoint.chapter !== endPoint.chapter) {
      return undefined;
    }

    const { chapters } = getTranslationInfo();

    const versesInChapter = chapters[startPoint.book]?.[startPoint.chapter - 1];
    if (!versesInChapter) return undefined;

    if (startPoint.verse === 1 && endPoint.verse === versesInChapter) {
      return undefined;
    }

    return `${formatUsfmPoint({ ...startPoint, verse: 1 })}-${formatUsfmPoint({ ...startPoint, verse: versesInChapter })}`;
  } catch {
    return undefined;
  }
};
